import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";
import ImageActions from "../ModalImageEdit/single/ImageActions";
import { fitTextInBox } from "../../../features/creativeTemplates/instance/fitTextInBox";
import {
  isItalicTextStyle,
  isUnderlineTextStyle,
  resolveTextShadow,
} from "../../../features/creativeTemplates/utils/textStyle";
import { resolveTextOrientation } from "../../../features/creativeTemplates/utils/textOrientation";
import FittedTextPreview from "../../../features/creativeTemplates/utils/FittedTextPreview";
import { canHighlightTextSource } from "../../../features/creativeTemplates/utils/textHighlights";
import { useInlineReplacementMetrics } from "../../../features/creativeTemplates/utils/useInlineReplacementMetrics";
import ZoomControls from "../../../features/creativeTemplates/editor/ZoomControls";
import AlignmentGuidesToggle from "../../../features/creativeTemplates/editor/AlignmentGuidesToggle";
import {
  CANVAS_VIEWPORT_PADDING,
  canvasZoomBoardSx,
  getCanvasZoomLayout,
  layoutBoxToVisual,
  useCanvasZoom,
} from "../../../features/creativeTemplates/editor/useCanvasZoom";
import { useFreezeViewportScroll } from "../../../features/creativeTemplates/editor/useFreezeViewportScroll";
import SelectionChromeOverlay, {
  CANVAS_SELECTION_HANDLE_CLASS,
} from "../../../features/creativeTemplates/editor/SelectionChromeOverlay";
import AlignmentGuidesOverlay from "../../../features/creativeTemplates/editor/AlignmentGuidesOverlay";
import RotationAngleOverlay, {
  syncRotationAngleGuide,
} from "../../../features/creativeTemplates/editor/RotationAngleOverlay";
import {
  applyDragAlignmentSnap,
  boxesOutsideDraggedBranch,
} from "../../../features/creativeTemplates/editor/alignmentGuides";
import { resizePixelBoxFromHandle } from "../../../features/creativeTemplates/editor/resizePixelBoxFromHandle";
import {
  canCropImageLayer,
  cropPixelBoxFromHandle,
  isCropHandleGesture,
} from "../../../features/creativeTemplates/editor/layerCrop";
import { CroppedLayerImage } from "../../../features/creativeTemplates/editor/CroppedLayerImage";
import { useShiftHeld } from "../../../features/creativeTemplates/editor/useShiftHeld";
import {
  getLiveMarqueeSelection,
  isAdditiveLayerSelect,
} from "../../../features/creativeTemplates/editor/canvasLayerSelection";
import {
  idsIntersectingMarquee,
  MARQUEE_MIN_SIZE_PX,
  marqueeOverlaySx,
  normalizeMarqueeRect,
} from "../../../features/creativeTemplates/editor/canvasMarquee";
import {
  GROUP_SELECTION_ID,
  axisAlignedBounds,
  groupSelectionChrome,
  scaleBoxesToBounds,
  translateBoxes,
} from "../../../features/creativeTemplates/editor/canvasGroupTransform";
import {
  canvasArtboardSx,
  canvasWorkspaceSx,
  canvasZoomHideScrollbarsSx,
  canvasZoomViewportSx,
} from "../../../features/creativeTemplates/editor/canvasWorkspace";
import { pickTopmostCanvasHit } from "../../../utils/pickCanvasHit";
import {
  ROTATE_HANDLE_CORNER,
  cssRotateTransform,
  pointInRotatedBox,
  rotationDegreesFromDrag,
  unrotatePoint,
  boxCenter,
} from "../../../features/creativeTemplates/editor/layerRotation";

const MIN_LAYER_SIZE_PX = 8;
const SELECTION_COLOR = "#111";

const clientToCanvasPixels = (event, canvas) => {
  const rect = canvas.getBoundingClientRect();
  if (!rect.width || !rect.height) {
    return { x: 0, y: 0 };
  }
  return {
    x: ((event.clientX - rect.left) / rect.width) * canvas.offsetWidth,
    y: ((event.clientY - rect.top) / rect.height) * canvas.offsetHeight,
  };
};

const constrainBoxMinSize = (box) => ({
  ...box,
  width: Math.max(box.width, MIN_LAYER_SIZE_PX),
  height: Math.max(box.height, MIN_LAYER_SIZE_PX),
});

const artboardToDisplay = ({ box, scale }) => ({
  x: box.x * scale,
  y: box.y * scale,
  width: box.width * scale,
  height: box.height * scale,
  rotation: Number(box.rotation) || 0,
});

const hitTestItem = ({ items, point, scale, preferText = false }) => {
  const hits = items
    .filter((item) => item.box.visible !== false)
    .map((item, paintIndex) => {
      const box = artboardToDisplay({ box: item.box, scale });
      return {
        item,
        box,
        isBackground: Boolean(item.isBackground),
        paintIndex,
        zIndex: item.box.zIndex,
        area: box.width * box.height,
      };
    })
    .filter(({ box }) => pointInRotatedBox({ point, box }));

  if (preferText) {
    const textHit = pickTopmostCanvasHit({
      hits: hits.filter((hit) => hit.item.node?.type === "text"),
    });
    if (textHit) return textHit.item;
  }

  return pickTopmostCanvasHit({ hits })?.item || null;
};

const isResizeHandleEvent = (event) =>
  Boolean(
    event.target instanceof Element &&
      event.target.closest(`.${CANVAS_SELECTION_HANDLE_CLASS}`)
  );

const canSelectTextInItem = (item) =>
  item?.node?.type === "text" &&
  canHighlightTextSource(item?.node?.source?.kind);

const LayerContent = ({
  item,
  isEditingText,
  onTextCommit,
  scale,
  isSelectingText = false,
  selectedPhrase = "",
  onSelectionChange,
}) => {
  const node = item.node;
  const inlineImages =
    node.type === "text" ? item.inlineReplacementUrls || {} : {};
  const replacementMetrics = useInlineReplacementMetrics({
    urls: inlineImages,
  });
  switch (node.type) {
    case "image":
      return item.url ? (
        <CroppedLayerImage
          src={item.url}
          alt={node.name || ""}
          crop={item.crop ?? node.crop}
          objectFit={node.fit === "cover" ? "cover" : "fill"}
        />
      ) : null;
    case "shape":
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            bgcolor: node.shape?.fill || "#000",
            borderRadius: `${Number(node.shape?.radius) || 0}px`,
            pointerEvents: "none",
          }}
        />
      );
    case "group":
      return null;
    case "text": {
      const orientation = resolveTextOrientation(node.style?.orientation);
      const fontFamily = node.style?.fontAssetRef || "sans-serif";
      const fontWeight =
        Number(node.style?.fontWeight) > 0
          ? Number(node.style.fontWeight)
          : 400;
      const requestedFontSize = Number(node.style?.fontSize);
      const hasRequestedFontSize =
        Number.isFinite(requestedFontSize) && requestedFontSize > 0;
      const italic = isItalicTextStyle(node.style?.textStyle);
      const underline = isUnderlineTextStyle(node.style?.textStyle);
      const letterSpacing = Number(node.style?.letterSpacing);
      const layout = fitTextInBox({
        text: item.text,
        boxWidth: item.box.width,
        boxHeight: item.box.height,
        fontFamily,
        minimumLines: node.overflow?.minimumLines,
        maximumLines: node.overflow?.maximumLines,
        minimumFontSize: node.overflow?.minimumFontSize,
        requestedFontSize: hasRequestedFontSize ? requestedFontSize : undefined,
        lineHeightMult: node.style?.lineHeight,
        letterSpacing: Number.isFinite(letterSpacing)
          ? letterSpacing
          : undefined,
        orientation,
        italic,
        underline,
        fontWeight,
        highlights: canHighlightTextSource(node.source?.kind)
          ? node.style?.highlights
          : undefined,
        strokeWidth: Number(node.style?.strokeWidth) || 0,
        inlineReplacements: node.style?.inlineReplacements,
        replacementMetrics,
      });
      return (
        <FittedTextPreview
          text={item.text}
          layout={layout}
          fallbackFontSize={hasRequestedFontSize ? requestedFontSize : 12}
          scale={scale}
          color={node.style?.color || "#fff"}
          fontFamily={fontFamily}
          fontWeight={fontWeight}
          align={node.style?.align || "left"}
          orientation={orientation}
          textStyle={node.style?.textStyle}
          letterSpacing={Number.isFinite(letterSpacing) ? letterSpacing : 0}
          strokeWidth={Number(node.style?.strokeWidth) || 0}
          strokeColor={node.style?.strokeColor}
          shadow={resolveTextShadow(node.style)}
          backgroundPadding={Number(node.style?.backgroundPadding) || 0}
          backgroundColor={node.style?.backgroundColor}
          backgroundRadius={node.style?.backgroundRadius}
          highlights={
            canHighlightTextSource(node.source?.kind)
              ? node.style?.highlights
              : undefined
          }
          inlineReplacements={node.style?.inlineReplacements}
          replacementMetrics={replacementMetrics}
          inlineImages={inlineImages}
          isEditing={isEditingText}
          onCommit={(nextText) => onTextCommit(item.id, nextText)}
          isSelectingText={isSelectingText}
          selectedPhrase={selectedPhrase}
          onSelectionChange={onSelectionChange}
        />
      );
    }
    default: {
      const exhaustive = node.type;
      throw new Error(`Unhandled node type: ${exhaustive}`);
    }
  }
};

const MovableLayer = memo(function MovableLayer({
  item,
  pixelBox,
  isSelected,
  isEditingText,
  paintIndex,
  onTextCommit,
  scale,
  isSelectingText = false,
  selectedPhrase = "",
  onTextSelectionChange,
}) {
  return (
    <Box
      sx={{
        position: "absolute",
        left: pixelBox.x,
        top: pixelBox.y,
        width: Math.max(pixelBox.width, MIN_LAYER_SIZE_PX),
        height: Math.max(pixelBox.height, MIN_LAYER_SIZE_PX),
        zIndex: paintIndex + 1,
        border: isSelected
          ? "1.5px solid transparent"
          : "1px solid transparent",
        pointerEvents: "auto",
        overflow: "visible",
        boxSizing: "border-box",
        transform: cssRotateTransform(pixelBox.rotation ?? item.box.rotation),
        transformOrigin: "center center",
      }}
      onPointerDown={(event) => {
        if (isEditingText) {
          event.stopPropagation();
        }
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          // Text clips itself, wide enough to keep a highlight pill whole where
          // it hugs past the layer box; anything else stays bound to the box.
          overflow: item.node?.type === "text" ? "visible" : "hidden",
          pointerEvents: isEditingText || isSelectingText ? "auto" : "none",
          userSelect: isSelectingText ? "text" : undefined,
          cursor: isSelectingText ? "text" : undefined,
        }}
      >
        <LayerContent
          item={item}
          isEditingText={isEditingText}
          onTextCommit={onTextCommit}
          scale={scale}
          isSelectingText={isSelectingText}
          selectedPhrase={selectedPhrase}
          onSelectionChange={(state) => onTextSelectionChange?.(item.id, state)}
        />
      </Box>
    </Box>
  );
});

const InstanceArtboardLayers = memo(function InstanceArtboardLayers({
  items,
  scale,
  selectedSet,
  editingTextID,
  selectedTextNodeID,
  selectedTextPhrase,
  onTextCommit,
  onTextSelectionChange,
  dragBoxes,
  dragLayerId,
  dragBox,
  displayWidth,
  displayHeight,
}) {
  return items.map((item, index) => {
    if (item.box.visible === false) return null;
    const pixelBox = dragBoxes?.[item.id]
      ? dragBoxes[item.id]
      : dragLayerId === item.id
        ? dragBox
        : item.isBackground
          ? {
              x: 0,
              y: 0,
              width: displayWidth,
              height: displayHeight,
            }
          : artboardToDisplay({ box: item.box, scale });
    const isSelected = selectedSet.includes(item.id);

    if (item.isBackground) {
      return (
        <Box
          key={item.id}
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
          }}
        >
          <Box
            component="img"
            src={item.url}
            alt={item.node.name || ""}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "fill",
              pointerEvents: "none",
              display: "block",
            }}
          />
        </Box>
      );
    }

    const displayItem = {
      ...item,
      crop: Object.prototype.hasOwnProperty.call(pixelBox || {}, "crop")
        ? pixelBox.crop
        : item.node?.crop,
    };
    return (
      <MovableLayer
        key={item.id}
        item={displayItem}
        pixelBox={pixelBox}
        isSelected={isSelected}
        isEditingText={editingTextID === item.id}
        isSelectingText={
          editingTextID === item.id && canSelectTextInItem(displayItem)
        }
        selectedPhrase={
          selectedTextNodeID === item.id ? selectedTextPhrase : ""
        }
        onTextSelectionChange={onTextSelectionChange}
        paintIndex={index}
        onTextCommit={onTextCommit}
        scale={scale}
      />
    );
  });
});

const InstanceCanvas = ({
  items = [],
  canvasWidth,
  canvasHeight,
  selectedLayerId,
  selectedLayerIds = [],
  editingTextID,
  onTextSelectionChange,
  selectedTextNodeID = null,
  selectedTextPhrase = "",
  isSidebarOpen,
  previewUrl = "",
  onToggleSidebar,
  onSelectLayer,
  onSelectLayers,
  onLayerBoxChange,
  onLayerBoxesChange,
  onStartTextEdit,
  onTextCommit,
  onClose,
  onCallbackSave = () => {},
  saveDisabled = true,
  showCloseButton = true,
  showSaveActions = true,
}) => {
  const viewportRef = useRef(null);
  const artboardRef = useRef(null);
  const [activeDrag, setActiveDrag] = useState(null);
  const activeDragRef = useRef(null);
  const [marquee, setMarquee] = useState(null);
  const [alignmentGuidesEnabled, setAlignmentGuidesEnabled] = useState(true);
  const marqueeRef = useRef(null);
  const shiftHeld = useShiftHeld();
  const alignmentGuidesRef = useRef(null);
  const rotationAngleRef = useRef(null);
  const snapContextRef = useRef({
    getOtherBoxes: () => [],
    artboardWidth: 0,
    artboardHeight: 0,
    minSize: MIN_LAYER_SIZE_PX,
  });
  activeDragRef.current = activeDrag;
  const { zoom, viewportSize, onZoomIn, onZoomOut, onFit } = useCanvasZoom({
    viewportRef,
    boardRef: artboardRef,
    resetKey: `${canvasWidth}x${canvasHeight}`,
    lockRef: activeDragRef,
  });
  useFreezeViewportScroll({
    viewportRef,
    frozen: Boolean(activeDrag) || Boolean(marquee),
  });
  const aspect = canvasWidth && canvasHeight ? canvasWidth / canvasHeight : 1;
  const { fitWidth, fitHeight } = useMemo(() => {
    const pad = CANVAS_VIEWPORT_PADDING * 2;
    const availableWidth = Math.max(1, viewportSize.width - pad);
    const availableHeight = Math.max(1, viewportSize.height - pad);
    if (availableWidth / availableHeight > aspect) {
      return {
        fitHeight: availableHeight,
        fitWidth: availableHeight * aspect,
      };
    }
    return {
      fitWidth: availableWidth,
      fitHeight: availableWidth / aspect,
    };
  }, [aspect, viewportSize.height, viewportSize.width]);
  const { padX, padY, contentWidth, contentHeight } = getCanvasZoomLayout({
    viewportWidth: viewportSize.width,
    viewportHeight: viewportSize.height,
    fitWidth,
    fitHeight,
    zoom,
  });
  const displaySize = {
    width: viewportSize.width ? fitWidth : 0,
    height: viewportSize.height ? fitHeight : 0,
  };
  const scale =
    canvasWidth && displaySize.width ? displaySize.width / canvasWidth : 1;
  snapContextRef.current = {
    getOtherBoxes: (excludeId) =>
      boxesOutsideDraggedBranch({
        boxes: items.filter((item) => item.box?.visible !== false),
        excludeId,
      }).map((item) => artboardToDisplay({ box: item.box, scale })),
    artboardWidth: displaySize.width,
    artboardHeight: displaySize.height,
    minSize: MIN_LAYER_SIZE_PX,
    guideScale: zoom,
  };

  const committedSelectedIDs = useMemo(() => {
    if (selectedLayerIds.length) return selectedLayerIds;
    return selectedLayerId ? [selectedLayerId] : [];
  }, [selectedLayerId, selectedLayerIds]);

  const itemToPixelBox = (item) => artboardToDisplay({ box: item.box, scale });

  const isMarqueeSelecting =
    Boolean(marquee) &&
    ((Number(marquee.w) || 0) >= MARQUEE_MIN_SIZE_PX ||
      (Number(marquee.h) || 0) >= MARQUEE_MIN_SIZE_PX);
  const marqueeHitIds = isMarqueeSelecting
    ? idsIntersectingMarquee({
        boxes: items,
        marquee,
        toBounds: (item) => itemToPixelBox(item),
        skip: (item) => item.isBackground || item.box.visible === false,
      })
    : [];
  const liveSelection = getLiveMarqueeSelection({
    committedIDs: committedSelectedIDs,
    committedPrimary: selectedLayerId,
    hitIds: marqueeHitIds,
    additive: Boolean(marquee?.additive),
    active: isMarqueeSelecting,
  });
  const selectedSet = liveSelection.ids;
  const livePrimaryID = liveSelection.primary;

  const commitDragBoxes = (drag) => {
    if (!displaySize.width || !displaySize.height || !canvasWidth) return;
    const boxesMap =
      drag.boxes ||
      (drag.box && drag.layerId ? { [drag.layerId]: drag.box } : {});
    const updates = Object.entries(boxesMap).map(([id, box]) => ({
      id,
      nx: box.x / displaySize.width,
      ny: box.y / displaySize.height,
      nw: box.width / displaySize.width,
      nh: box.height / displaySize.height,
      rotation: box.rotation,
      ...(Object.prototype.hasOwnProperty.call(box, "crop")
        ? { crop: box.crop }
        : {}),
    }));
    if (!updates.length) return;
    if (typeof onLayerBoxesChange === "function") {
      onLayerBoxesChange(updates);
      return;
    }
    updates.forEach((update) => onLayerBoxChange(update));
  };
  const commitDragBoxesRef = useRef(commitDragBoxes);
  commitDragBoxesRef.current = commitDragBoxes;

  const onArtboardPointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (editingTextID) return;
    if (isResizeHandleEvent(event)) return;
    const canvas = artboardRef.current;
    if (!canvas || !displaySize.width) return;

    const point = clientToCanvasPixels(event, canvas);
    const hitItem = hitTestItem({
      items,
      point,
      scale,
    });
    if (!hitItem) return;

    event.preventDefault();
    onSelectLayer(hitItem.id, event, { preserveMulti: true });
    if (isAdditiveLayerSelect(event)) return;
    if (hitItem.isBackground || hitItem.node?.locked) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    const selectedMoving = items.filter(
      (item) =>
        selectedSet.includes(item.id) &&
        item.box.visible !== false &&
        !item.isBackground &&
        !item.node?.locked
    );
    const moving =
      selectedSet.includes(hitItem.id) && selectedMoving.length > 1
        ? selectedMoving
        : [hitItem];
    const originBoxes = moving.map((item) => ({
      id: item.id,
      ...itemToPixelBox(item),
    }));
    const boxes = Object.fromEntries(originBoxes.map((box) => [box.id, box]));
    const box = boxes[hitItem.id];
    const nextDrag = {
      layerId: hitItem.id,
      ids: moving.map((item) => item.id),
      mode: "move",
      box,
      boxes,
      originBoxes,
      startPoint: point,
      grabOffset: {
        x: point.x - box.x,
        y: point.y - box.y,
      },
    };
    activeDragRef.current = nextDrag;
    setActiveDrag(nextDrag);
  };

  const onArtboardPointerMove = (event) => {
    if (!event.currentTarget.hasPointerCapture?.(event.pointerId)) return;
    const canvas = artboardRef.current;
    if (!canvas) return;
    const current = activeDragRef.current;
    if (
      !current ||
      current.mode === "resize" ||
      current.mode === "rotate" ||
      current.mode === "crop"
    )
      return;
    const point = clientToCanvasPixels(event, canvas);
    if ((current.ids || []).length > 1) {
      const dx = point.x - current.startPoint.x;
      const dy = point.y - current.startPoint.y;
      const moved = translateBoxes({
        boxes: current.originBoxes,
        dx,
        dy,
      });
      const boxes = Object.fromEntries(moved.map((box) => [box.id, box]));
      const next = {
        ...current,
        boxes,
        box: boxes[current.layerId],
      };
      activeDragRef.current = next;
      setActiveDrag(next);
      alignmentGuidesRef.current?.clear();
      return;
    }
    const proposed = constrainBoxMinSize({
      ...current.box,
      x: point.x - current.grabOffset.x,
      y: point.y - current.grabOffset.y,
    });
    const snapped = applyDragAlignmentSnap({
      box: proposed,
      mode: "move",
      event,
      excludeId: current.layerId,
      prevSnap: current.snap,
      snapContext: snapContextRef.current,
      guidesRef: alignmentGuidesRef,
      enabled: alignmentGuidesEnabled,
    });
    const next = {
      ...current,
      box: snapped.box,
      boxes: { [current.layerId]: snapped.box },
      snap: snapped.snap,
    };
    activeDragRef.current = next;
    setActiveDrag(next);
  };

  const onArtboardPointerUp = (event) => {
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const current = activeDragRef.current;
    if (
      !current ||
      current.mode === "resize" ||
      current.mode === "rotate" ||
      current.mode === "crop"
    )
      return;
    activeDragRef.current = null;
    setActiveDrag(null);
    alignmentGuidesRef.current?.clear();
    commitDragBoxes(current);
  };

  const onPointerDownWorkspace = (event) => {
    if (editingTextID) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (typeof onSelectLayers !== "function") return;
    if (artboardRef.current?.contains(event.target)) return;
    if (isResizeHandleEvent(event)) return;
    const canvas = artboardRef.current;
    if (!canvas || !displaySize.width) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const point = clientToCanvasPixels(event, canvas);
    const next = {
      x: point.x,
      y: point.y,
      w: 0,
      h: 0,
      originX: point.x,
      originY: point.y,
      additive: isAdditiveLayerSelect(event),
    };
    marqueeRef.current = next;
    setMarquee(next);
  };

  const onPointerMoveWorkspace = (event) => {
    const current = marqueeRef.current;
    if (!current) return;
    const canvas = artboardRef.current;
    if (!canvas) return;
    const point = clientToCanvasPixels(event, canvas);
    const next = {
      originX: current.originX,
      originY: current.originY,
      additive: isAdditiveLayerSelect(event),
      ...normalizeMarqueeRect({
        originX: current.originX,
        originY: current.originY,
        x: point.x,
        y: point.y,
      }),
    };
    marqueeRef.current = next;
    setMarquee(next);
  };

  const onPointerUpWorkspace = (event) => {
    const current = marqueeRef.current;
    marqueeRef.current = null;
    setMarquee(null);
    if (!current || typeof onSelectLayers !== "function") return;
    const ids = idsIntersectingMarquee({
      boxes: items,
      marquee: current,
      toBounds: (item) => itemToPixelBox(item),
      skip: (item) => item.isBackground || item.box.visible === false,
    });
    onSelectLayers(ids, event);
  };

  const onOverlayHandlePointerDown = ({ event, id, corner }) => {
    event.preventDefault();
    event.stopPropagation();
    if (editingTextID) return;
    const canvas = artboardRef.current;
    if (!canvas || !displaySize.width) return;
    const point = clientToCanvasPixels(event, canvas);
    if (id === GROUP_SELECTION_ID) {
      const moving = items.filter(
        (item) =>
          selectedSet.includes(item.id) &&
          item.box.visible !== false &&
          !item.isBackground &&
          !item.node?.locked
      );
      if (moving.length < 2) return;
      const originBoxes = moving.map((item) => ({
        id: item.id,
        ...itemToPixelBox(item),
      }));
      const boxes = Object.fromEntries(originBoxes.map((box) => [box.id, box]));
      const nextDrag = {
        layerId: moving[0].id,
        ids: moving.map((item) => item.id),
        mode: "resize",
        corner,
        startPoint: point,
        originBoxes,
        originBounds: axisAlignedBounds(originBoxes),
        boxes,
        box: boxes[moving[0].id],
      };
      activeDragRef.current = nextDrag;
      setActiveDrag(nextDrag);
      return;
    }
    const item = items.find((entry) => entry.id === id);
    if (!item || item.isBackground || item.node?.locked) return;
    const box = itemToPixelBox(item);
    const isCrop =
      corner !== ROTATE_HANDLE_CORNER &&
      isCropHandleGesture({
        event,
        shiftHeld,
        layer: {
          type: item.node?.type,
          isBackground: item.isBackground,
        },
        selectedCount: 1,
      });
    const nextDrag = {
      layerId: item.id,
      ids: [item.id],
      mode:
        corner === ROTATE_HANDLE_CORNER ? "rotate" : isCrop ? "crop" : "resize",
      corner,
      startPoint: point,
      originBox: box,
      originBoxes: [{ id: item.id, ...box }],
      originRotation: Number(box.rotation) || 0,
      originCrop: item.node?.crop,
      box,
      boxes: { [item.id]: box },
    };
    activeDragRef.current = nextDrag;
    setActiveDrag(nextDrag);
    if (nextDrag.mode === "rotate") {
      syncRotationAngleGuide(rotationAngleRef, {
        degrees: nextDrag.originRotation,
        event,
      });
    }
  };

  useEffect(() => {
    const onMove = (event) => {
      const current = activeDragRef.current;
      if (
        !current ||
        (current.mode !== "resize" &&
          current.mode !== "rotate" &&
          current.mode !== "crop")
      ) {
        return;
      }
      const canvas = artboardRef.current;
      if (!canvas) return;
      const point = clientToCanvasPixels(event, canvas);
      if ((current.ids || []).length > 1 && current.mode === "resize") {
        const nextBounds = constrainBoxMinSize(
          resizePixelBoxFromHandle({
            origin: current.originBounds,
            corner: current.corner,
            startPoint: current.startPoint,
            point,
            minSize: MIN_LAYER_SIZE_PX,
          })
        );
        const scaled = scaleBoxesToBounds({
          boxes: current.originBoxes,
          originBounds: current.originBounds,
          nextBounds,
          minSize: MIN_LAYER_SIZE_PX,
        });
        const boxes = Object.fromEntries(scaled.map((box) => [box.id, box]));
        const next = {
          ...current,
          boxes,
          box: boxes[current.layerId],
        };
        activeDragRef.current = next;
        setActiveDrag(next);
        alignmentGuidesRef.current?.clear();
        return;
      }
      const originRotation = Number(current.originRotation) || 0;
      let nextBox = current.box;
      if (current.mode === "crop") {
        const originBox = current.originBox;
        const center = boxCenter(originBox);
        const cropped = cropPixelBoxFromHandle({
          originFrame: originBox,
          originCrop: current.originCrop,
          corner: current.corner,
          startPoint: originRotation
            ? unrotatePoint({
                point: current.startPoint,
                center,
                degrees: originRotation,
              })
            : current.startPoint,
          point: originRotation
            ? unrotatePoint({
                point,
                center,
                degrees: originRotation,
              })
            : point,
          minSize: MIN_LAYER_SIZE_PX,
        });
        nextBox = {
          x: cropped.x,
          y: cropped.y,
          width: cropped.width,
          height: cropped.height,
          rotation: originRotation,
          crop: cropped.crop,
        };
        const next = {
          ...current,
          box: nextBox,
          boxes: { [current.layerId]: nextBox },
        };
        activeDragRef.current = next;
        setActiveDrag(next);
        alignmentGuidesRef.current?.clear();
        return;
      }
      if (current.mode === "rotate") {
        nextBox = {
          ...current.originBox,
          rotation: rotationDegreesFromDrag({
            center: boxCenter(current.originBox),
            startPoint: current.startPoint,
            currentPoint: point,
            originRotation,
          }),
        };
        syncRotationAngleGuide(rotationAngleRef, {
          degrees: nextBox.rotation,
          event,
        });
      } else if (!originRotation) {
        nextBox = constrainBoxMinSize(
          resizePixelBoxFromHandle({
            origin: current.originBox,
            corner: current.corner,
            startPoint: current.startPoint,
            point,
            minSize: MIN_LAYER_SIZE_PX,
          })
        );
        nextBox.rotation = originRotation;
      } else {
        const center = boxCenter(current.originBox);
        nextBox = constrainBoxMinSize(
          resizePixelBoxFromHandle({
            origin: current.originBox,
            corner: current.corner,
            startPoint: unrotatePoint({
              point: current.startPoint,
              center,
              degrees: originRotation,
            }),
            point: unrotatePoint({
              point,
              center,
              degrees: originRotation,
            }),
            minSize: MIN_LAYER_SIZE_PX,
          })
        );
        nextBox.rotation = originRotation;
      }
      const snapped = applyDragAlignmentSnap({
        box: nextBox,
        mode: current.mode,
        corner: current.corner,
        event,
        excludeId: current.layerId,
        prevSnap: current.snap,
        snapContext: snapContextRef.current,
        guidesRef: alignmentGuidesRef,
        enabled: alignmentGuidesEnabled,
      });
      const next = {
        ...current,
        box: snapped.box,
        boxes: { [current.layerId]: snapped.box },
        snap: snapped.snap,
      };
      activeDragRef.current = next;
      setActiveDrag(next);
    };
    const onUp = () => {
      const current = activeDragRef.current;
      if (
        !current ||
        (current.mode !== "resize" &&
          current.mode !== "rotate" &&
          current.mode !== "crop")
      ) {
        return;
      }
      activeDragRef.current = null;
      setActiveDrag(null);
      alignmentGuidesRef.current?.clear();
      rotationAngleRef.current?.clear();
      commitDragBoxesRef.current(current);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    //eslint-disable-next-line
  }, []);

  const livePixelBoxForItem = (item) => {
    if (!item) return null;
    if (activeDrag?.boxes?.[item.id]) return activeDrag.boxes[item.id];
    if (activeDrag?.layerId === item.id) return activeDrag.box;
    if (item.isBackground) {
      return {
        x: 0,
        y: 0,
        width: displaySize.width,
        height: displaySize.height,
      };
    }
    return itemToPixelBox(item);
  };
  const selectionChrome = useMemo(() => {
    if (!displaySize.width || !displaySize.height) return [];
    const selectedItems = items.filter(
      (item) => selectedSet.includes(item.id) && item.box.visible !== false
    );
    if (!selectedItems.length) return [];
    const transformableSelected = selectedItems.filter(
      (item) => !item.isBackground && !item.node?.locked
    );
    const isGroupTransform = transformableSelected.length > 1;
    const layerChrome = selectedItems.map((item) => {
      const box = layoutBoxToVisual({
        box: livePixelBoxForItem(item),
        zoom,
      });
      const isPrimary = livePrimaryID === item.id;
      return {
        id: item.id,
        x: box.x,
        y: box.y,
        width: box.width,
        height: box.height,
        rotation: Number(box.rotation) || 0,
        color: SELECTION_COLOR,
        name: item.node?.name || item.id,
        showLabel: isPrimary && !isGroupTransform,
        showHandles:
          isPrimary &&
          !isGroupTransform &&
          !isMarqueeSelecting &&
          !item.isBackground &&
          !item.node?.locked &&
          editingTextID !== item.id,
        cropHandles:
          (activeDrag?.mode === "crop" || (!activeDrag && shiftHeld)) &&
          canCropImageLayer({
            type: item.node?.type,
            isBackground: item.isBackground,
          }),
      };
    });
    const groupChrome = isGroupTransform
      ? groupSelectionChrome({
          bounds: axisAlignedBounds(
            transformableSelected.map((item) =>
              layoutBoxToVisual({
                box: livePixelBoxForItem(item),
                zoom,
              })
            )
          ),
          showHandles: !isMarqueeSelecting,
        })
      : null;
    return groupChrome ? [...layerChrome, groupChrome] : layerChrome;
    // eslint-disable-next-line
  }, [
    activeDrag,
    displaySize.height,
    displaySize.width,
    editingTextID,
    isMarqueeSelecting,
    items,
    livePrimaryID,
    selectedSet,
    shiftHeld,
    zoom,
  ]);

  const onArtboardDoubleClick = (event) => {
    const canvas = artboardRef.current;
    if (!canvas || !displaySize.width) return;
    const point = clientToCanvasPixels(event, canvas);
    const hitItem = hitTestItem({
      items,
      point,
      scale,
      preferText: true,
    });
    if (hitItem?.node?.type === "text" && !hitItem.node?.locked) {
      onSelectLayer(hitItem.id, event);
      onStartTextEdit(hitItem.id);
    }
  };

  return (
    <Box
      data-instance-canvas
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        height: "100%",
        maxHeight: "100%",
      }}
    >
      <Box
        sx={{
          flex: 1,
          px: { xs: 2, sm: 4 },
          pt: { xs: showCloseButton ? 6 : 2, sm: showCloseButton ? 6 : 2 },
          pb: { xs: 1, sm: 2 },
          overflow: "hidden",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          ...canvasWorkspaceSx,
        }}
      >
        {!isSidebarOpen ? (
          <IconButton
            onClick={onToggleSidebar}
            sx={{
              position: "absolute",
              top: 15,
              left: 10,
              zIndex: 10,
            }}
          >
            <MenuIcon />
          </IconButton>
        ) : null}

        {showCloseButton ? (
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 15,
              right: 6,
              zIndex: 10,
            }}
          >
            <CloseIcon />
          </IconButton>
        ) : null}

        <Box
          sx={{
            position: "relative",
            flex: 1,
            minHeight: 0,
            width: "100%",
            height: "100%",
          }}
        >
          <Box
            ref={viewportRef}
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              flex: 1,
              minHeight: 0,
              maxWidth: "100%",
              maxHeight: "100%",
              ...canvasZoomViewportSx,
              ...(zoom <= 1.001 ? canvasZoomHideScrollbarsSx : null),
            }}
          >
            <Box
              onPointerDown={onPointerDownWorkspace}
              onPointerMove={onPointerMoveWorkspace}
              onPointerUp={onPointerUpWorkspace}
              onPointerCancel={onPointerUpWorkspace}
              sx={{
                position: "relative",
                width: contentWidth,
                height: contentHeight,
                overflow: "visible",
                touchAction: "none",
              }}
            >
              {displaySize.width > 0 ? (
                <Box
                  ref={artboardRef}
                  onPointerDown={onArtboardPointerDown}
                  onPointerMove={onArtboardPointerMove}
                  onPointerUp={onArtboardPointerUp}
                  onPointerCancel={onArtboardPointerUp}
                  onDoubleClick={onArtboardDoubleClick}
                  sx={{
                    isolation: "isolate",
                    overflow: "visible",
                    ...canvasArtboardSx,
                    ...canvasZoomBoardSx({
                      padX,
                      padY,
                      fitWidth,
                      fitHeight,
                      zoom,
                    }),
                    borderRadius: 2,
                    touchAction: "none",
                  }}
                >
                  <InstanceArtboardLayers
                    items={items}
                    scale={scale}
                    selectedSet={selectedSet}
                    editingTextID={editingTextID}
                    selectedTextNodeID={selectedTextNodeID}
                    selectedTextPhrase={selectedTextPhrase}
                    onTextCommit={onTextCommit}
                    onTextSelectionChange={onTextSelectionChange}
                    dragBoxes={activeDrag?.boxes}
                    dragLayerId={activeDrag?.layerId}
                    dragBox={activeDrag?.box}
                    displayWidth={displaySize.width}
                    displayHeight={displaySize.height}
                  />
                </Box>
              ) : null}
              {marquee ? (
                <Box
                  sx={marqueeOverlaySx({
                    x: padX + marquee.x,
                    y: padY + marquee.y,
                    w: marquee.w,
                    h: marquee.h,
                  })}
                />
              ) : null}
            </Box>
          </Box>
          <SelectionChromeOverlay
            boardRef={artboardRef}
            viewportRef={viewportRef}
            selections={selectionChrome}
            syncKey={`${zoom}-${displaySize.width}-${displaySize.height}-${selectedSet.join(",")}`}
            onHandlePointerDown={onOverlayHandlePointerDown}
          />
          <AlignmentGuidesOverlay
            ref={alignmentGuidesRef}
            boardRef={artboardRef}
            viewportRef={viewportRef}
            syncKey={`${zoom}-${displaySize.width}-${displaySize.height}`}
          />
          <RotationAngleOverlay ref={rotationAngleRef} />
        </Box>
        <ZoomControls
          zoom={zoom}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
          onFit={onFit}
          leading={
            <AlignmentGuidesToggle
              enabled={alignmentGuidesEnabled}
              onToggle={() => setAlignmentGuidesEnabled((value) => !value)}
            />
          }
        />
      </Box>

      {showSaveActions ? (
        <ImageActions
          imageUrl={previewUrl}
          initialImageUrl={previewUrl}
          onCallbackSave={onCallbackSave}
          onClose={onClose}
          showSaveAlert={false}
          disabled={saveDisabled}
        />
      ) : null}
    </Box>
  );
};

export default InstanceCanvas;
